/**
 * ClientSystemPicker — business-friendly Client + SAP System selector.
 *
 * Replaces the raw `clientId` text inputs across the PRIVA Governance screens
 * (Signal Health, SAP Connections, Signal Services, Tenant Config). Sourced
 * from the Client Master + SAP System Master — never asks the admin to type a
 * technical id.
 *
 *   mode="client"         → Business Client dropdown only (emits { clientId }).
 *   mode="client-system"  → Business Client + SAP System dropdowns
 *                           (emits { clientId, clientSapId }).
 *
 * The legacy `demo-client` (mock provider, no SAP system) is offered as an
 * explicit "Demo Client (Mock)" option so existing test flows keep working.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { listSmartewmClients, listSmartewmSapSystems } from "../lib/adminApi";
import EnvBadge, { ClientLogo } from "../components/EnvBadge";
import { systemLabel } from "../lib/smartewmLabels";

const DEMO_CLIENT = { clientId: "demo-client", clientName: "Demo Client (Mock)" };

export default function ClientSystemPicker({
  mode = "client",
  value,
  onChange,
  testIdPrefix = "ctx",
}) {
  const [clients, setClients] = useState([]);
  const [systems, setSystems] = useState([]);
  const clientId = value?.clientId || "";

  useEffect(() => {
    listSmartewmClients()
      .then((d) => {
        const list = [...(d.clients || []), DEMO_CLIENT];
        setClients(list);
        // Auto-select the first real client on first load when nothing is set.
        if (!clientId && list.length) onChange({ clientId: list[0].clientId, clientSapId: "" });
      })
      .catch(() => setClients([DEMO_CLIENT]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSystems = useCallback((cid, keepSapId) => {
    if (mode !== "client-system" || !cid || cid === "demo-client") {
      setSystems([]);
      return;
    }
    listSmartewmSapSystems(cid)
      .then((d) => {
        const list = d.sapSystems || [];
        setSystems(list);
        // Pick the default/first system unless the current one still belongs.
        const stillValid = keepSapId && list.some((s) => s.clientSapId === keepSapId);
        if (!stillValid) {
          const def = list.find((s) => s.isDefault) || list[0];
          onChange({ clientId: cid, clientSapId: def ? def.clientSapId : "" });
        }
      })
      .catch(() => setSystems([]));
  }, [mode, onChange]);

  useEffect(() => {
    if (clientId) loadSystems(clientId, value?.clientSapId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const onClientChange = (cid) => {
    onChange({ clientId: cid, clientSapId: "" });
  };
  const onSystemChange = (sid) => {
    onChange({ clientId, clientSapId: sid });
  };

  return (
    <div className="flex items-end gap-3">
      <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
        Business Client
        <Select value={clientId} onValueChange={onClientChange}>
          <SelectTrigger className="h-8 text-xs w-48" data-testid={`${testIdPrefix}-client-select`}>
            <SelectValue placeholder="Select client" />
          </SelectTrigger>
          <SelectContent>
            {clients.map((c) => (
              <SelectItem key={c.clientId} value={c.clientId}>
                <span className="inline-flex items-center gap-1.5">
                  <ClientLogo logo={c.logo} name={c.clientName} size={16} />
                  {c.clientName}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      {mode === "client-system" && (
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[#6B7280]">
          SAP System
          <Select
            value={value?.clientSapId || ""}
            onValueChange={onSystemChange}
            disabled={clientId === "demo-client" || systems.length === 0}
          >
            <SelectTrigger className="h-8 text-xs w-52" data-testid={`${testIdPrefix}-system-select`}>
              <SelectValue placeholder={clientId === "demo-client" ? "Mock (no SAP system)" : "Select SAP system"} />
            </SelectTrigger>
            <SelectContent>
              {systems.map((s) => (
                <SelectItem key={s.clientSapId} value={s.clientSapId}>
                  <span className="inline-flex items-center gap-1.5">
                    {systemLabel(s)}<EnvBadge env={s.environment} />
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      )}
    </div>
  );
}
